/**
 * 意图驱动动效引擎（8.1）。
 *
 * resolveMotionIntent：从自然语言描述（如 "hover 时卡片发光"）匹配 intent_keywords
 * 到配方；applyRecipe：把配方转成可注入组件的 style / className / animate props。
 * 底层动画库（GSAP / Framer Motion / Lottie）对用户透明，本层只产出标准产物。
 */
import type { CSSProperties } from "react";

import { MOTION_RECIPES } from "./presets";
import type { MotionRecipe, MotionTrigger } from "./types";

/** 注入到组件上的动效产物。 */
export interface AppliedMotion {
  /** 叠加 className：`dp-motion-<name>`，交互触发配方额外带 `dp-motion-trigger-<trigger>`。 */
  className?: string;
  /** 内联 style（animation/transition 相关属性）。 */
  style?: CSSProperties;
  /** Framer Motion 风格 `animate` prop（keyframes/transition 的终态），可选。 */
  animate?: Record<string, unknown>;
}

/** 挂载即播放的触发：inline style 直接带 animation。 */
const MOUNT_TRIGGERS: readonly MotionTrigger[] = ["load", "scroll-into-view", "navigation"];

/** 交互触发：靠 className + CSS export 的 :hover/:focus/:active 规则驱动。 */
const INTERACTION_TRIGGERS: readonly MotionTrigger[] = ["hover", "focus", "click", "error"];

/** CSS @keyframes 名。 */
export function cssAnimationName(recipeName: string): string {
  return `dp-${recipeName}`;
}

/** 配方注入的 className（不含用户已有 className）。 */
export function motionClassName(recipe: MotionRecipe): string {
  const classes = [`dp-motion-${recipe.name}`];
  if (INTERACTION_TRIGGERS.includes(recipe.recipe.trigger)) {
    classes.push(`dp-motion-trigger-${recipe.recipe.trigger}`);
  }
  return classes.join(" ");
}

/** 文本归一化：仅小写，供关键词匹配使用。 */
export function normalizeIntentText(text: string): string {
  return text.trim().toLowerCase();
}

export interface ResolveOptions {
  /** 自定义配方集合（默认内置配方），便于测试与扩展接入。 */
  recipes?: MotionRecipe[];
}

/**
 * 意图驱动解析：返回按命中关键词数降序排列的配方。
 * 无命中返回空数组（调用方视为"不动效"）。
 */
export function resolveMotionIntent(text: string, options: ResolveOptions = {}): MotionRecipe[] {
  const recipes = options.recipes ?? MOTION_RECIPES;
  const query = normalizeIntentText(text);
  if (!query) return [];

  return recipes
    .map((recipe) => ({
      recipe,
      hits: recipe.intentKeywords.filter((keyword) => {
        const kw = keyword.trim().toLowerCase();
        return kw.length > 0 && query.includes(kw);
      }).length,
    }))
    .filter((entry) => entry.hits > 0)
    .sort((a, b) => b.hits - a.hits)
    .map((entry) => entry.recipe);
}

/**
 * 把配方转成可注入组件的 props。
 *
 * - 挂载类触发（load/scroll-into-view/navigation）：style 携带 animation 简写；
 * - 交互类触发（hover/focus/click/error）：style 不带 animation，由 className +
 *   CSS export 中的 :hover/:focus/:active 规则驱动（避免挂载即播放）；
 * - transition 型配方：style 携带 transition 简写。
 *
 * @param recipe       要应用的配方
 * @param elementProps 元素已有 props（目前只关心 className 合并）
 */
export function applyRecipe(
  recipe: MotionRecipe,
  elementProps: { className?: string } = {},
): AppliedMotion {
  const className = [elementProps.className, motionClassName(recipe)].filter(Boolean).join(" ");
  const cfg = recipe.recipe;
  const applied: AppliedMotion = {};

  if (className) applied.className = className;

  if (cfg.kind === "transition") {
    const t = cfg.transition;
    if (t) {
      applied.style = {
        transition: `${t.property} ${t.duration}ms ${t.timingFunction}${t.delay ? ` ${t.delay}ms` : ""}`,
      };
    }
    if (cfg.to) applied.animate = { ...cfg.to };
    return applied;
  }

  if (MOUNT_TRIGGERS.includes(cfg.trigger)) {
    const style: CSSProperties = {
      animation: `${cssAnimationName(recipe.name)} ${cfg.duration}ms ${cfg.easing} ${cfg.delay}ms ${
        cfg.iterationCount ?? 1
      } ${cfg.fillMode ?? "both"}`,
    };
    if (cfg.transformOrigin) style.transformOrigin = cfg.transformOrigin;
    applied.style = style;
  }

  const endState = cfg.keyframes?.to ?? cfg.keyframes?.["100%"];
  if (endState) applied.animate = { ...endState };

  return applied;
}
